import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, input, signal, viewChild } from '@angular/core';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { TenantService } from '@/cloud/services/tenant.service';
import { AssistantService } from '@/cloud/services/assistant.service';
import { AssistantMention, SLASH_COMMANDS, SlashCommand, expandSlash, nextWord, predictCompletion } from '@/cloud/models/assistant.models';
import { TranslationService } from '@/layout/service/translation.service';

interface MentionOption extends AssistantMention {
    detail: string | null;
}

const MAX_MENTION_OPTIONS = 8;

/**
 * The prompt box, in the spirit of the Claude Code CLI: ghost-text prediction (Tab accepts, → takes a word),
 * slash commands, @mentions of devices and rooms, and ↑/↓ through earlier prompts.
 */
@Component({
    selector: 'app-assistant-composer',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (mentions().length) {
            <div class="mention-chips">
                @for (m of mentions(); track m.id) {
                    <span class="mention-chip" [attr.data-type]="m.type">
                        <i [class]="m.type === 'device' ? 'pi pi-server' : 'pi pi-map-marker'" aria-hidden="true"></i>{{ m.label }}
                        <button type="button" (click)="removeMention(m.id)" [attr.aria-label]="'Remove ' + m.label"><i class="pi pi-times" aria-hidden="true"></i></button>
                    </span>
                }
            </div>
        }

        @if (slashOptions().length) {
            <ul class="popover" role="listbox" [attr.aria-label]="t()('assistant.commands')">
                @for (c of slashOptions(); track c.command; let i = $index) {
                    <li role="option" [attr.aria-selected]="i === highlighted()" [class.active]="i === highlighted()" (mousedown)="$event.preventDefault(); chooseSlash(c)">
                        <code>{{ c.command }}</code
                        ><span>{{ c.description }}</span>
                    </li>
                }
            </ul>
        } @else if (mentionOptions().length) {
            <ul class="popover" role="listbox">
                @for (m of mentionOptions(); track m.id; let i = $index) {
                    <li role="option" [attr.aria-selected]="i === highlighted()" [class.active]="i === highlighted()" (mousedown)="$event.preventDefault(); chooseMention(m)">
                        <i [class]="m.type === 'device' ? 'pi pi-server' : 'pi pi-map-marker'" aria-hidden="true"></i>
                        <span class="opt-label">{{ m.label }}</span>
                        @if (m.detail) {
                            <span class="opt-detail">{{ m.detail }}</span>
                        }
                    </li>
                }
            </ul>
        }

        <div class="composer" [class.busy]="streaming()">
            <div class="field">
                <div class="ghost" aria-hidden="true">
                    <span class="typed">{{ text() }}</span
                    ><span class="prediction">{{ ghost() }}</span>
                </div>
                <textarea
                    #box
                    rows="1"
                    [value]="text()"
                    (input)="onInput($event)"
                    (keydown)="onKeyDown($event)"
                    (click)="refreshPopovers()"
                    [placeholder]="ghost() ? '' : t()('assistant.placeholder')"
                    [attr.aria-label]="t()('assistant.placeholder')"
                    aria-autocomplete="list"
                ></textarea>
            </div>
            @if (streaming()) {
                <button type="button" class="send stop" (click)="assistant.stop()" [attr.aria-label]="t()('assistant.stop')"><i class="pi pi-stop-circle" aria-hidden="true"></i></button>
            } @else {
                <button type="button" class="send" [disabled]="!text().trim()" (click)="submit()" [attr.aria-label]="t()('assistant.send')"><i class="pi pi-arrow-up" aria-hidden="true"></i></button>
            }
        </div>
        <div class="hint">{{ t()('assistant.placeholderHint') }}</div>
    `,
    styleUrl: './assistant-composer.component.scss'
})
export class AssistantComposerComponent {
    /** Starter prompts (from the pulse) offered as ghost text before the first answer. */
    readonly starters = input<string[]>([]);

    readonly assistant = inject(AssistantService);
    private api = inject(CloudApiService);
    private tenants = inject(TenantService);
    readonly t = inject(TranslationService).t;
    private box = viewChild<ElementRef<HTMLTextAreaElement>>('box');

    readonly text = signal('');
    readonly mentions = signal<AssistantMention[]>([]);
    readonly highlighted = signal(0);
    private readonly mentionQuery = signal<string | null>(null);
    /** Index into recent prompts while browsing with ↑/↓; -1 when not browsing. */
    private historyIndex = -1;
    private draft = '';

    readonly streaming = this.assistant.streaming;

    readonly ghost = computed(() => {
        const typed = this.text();
        if (this.mentionQuery() !== null || this.slashOptions().length > 1) return '';
        if (typed.startsWith('/'))
            return predictCompletion(
                typed,
                SLASH_COMMANDS.map((c) => c.command)
            );
        const candidates = [...this.assistant.suggestions(), ...(this.assistant.messages().length ? [] : this.starters()), ...this.assistant.recentPrompts()];
        return predictCompletion(typed, candidates);
    });

    readonly slashOptions = computed<SlashCommand[]>(() => {
        const typed = this.text();
        if (!/^\/[a-z]*$/.test(typed)) return [];
        return SLASH_COMMANDS.filter((c) => c.command.startsWith(typed));
    });

    readonly mentionOptions = computed<MentionOption[]>(() => {
        const query = this.mentionQuery();
        if (query === null) return [];
        const q = query.toLowerCase();
        const ns = this.assistant.scope() === 'tenant' ? this.tenants.selectedTenantId() : null;
        const chosen = new Set(this.mentions().map((m) => m.id));
        const devices: MentionOption[] = (this.api.devices() ?? []).filter((d) => !ns || d.appNamespaceId === ns).map((d) => ({ type: 'device' as const, id: d.id, label: d.displayName || d.name || d.id, detail: d.model ?? null }));
        const locations: MentionOption[] = ((this.api.locations() ?? []) as { id?: string; name?: string; appNamespaceId?: string; type?: string }[])
            .filter((l) => !!l?.id && (!ns || l.appNamespaceId === ns))
            .map((l) => ({ type: 'location' as const, id: l.id!, label: l.name || l.id!, detail: l.type ?? null }));
        return [...devices, ...locations]
            .filter((o) => !chosen.has(o.id) && (!q || o.label.toLowerCase().includes(q)))
            .sort((a, b) => Number(!a.label.toLowerCase().startsWith(q)) - Number(!b.label.toLowerCase().startsWith(q)) || a.label.localeCompare(b.label))
            .slice(0, MAX_MENTION_OPTIONS);
    });

    focus(): void {
        this.box()?.nativeElement.focus();
    }

    /** Fills the box (e.g. from a context action) without sending. */
    prefill(text: string): void {
        this.setText(text);
        this.focus();
    }

    onInput(event: Event): void {
        const el = event.target as HTMLTextAreaElement;
        this.text.set(el.value);
        this.historyIndex = -1;
        this.highlighted.set(0);
        this.refreshPopovers();
        this.autosize();
    }

    refreshPopovers(): void {
        const el = this.box()?.nativeElement;
        const caret = el?.selectionStart ?? this.text().length;
        const before = this.text().slice(0, caret);
        const match = /(?:^|\s)@([^\s@]{0,40})$/.exec(before);
        this.mentionQuery.set(match ? match[1] : null);
    }

    onKeyDown(event: KeyboardEvent): void {
        const options = this.slashOptions().length ? this.slashOptions().length : this.mentionOptions().length;

        if (options > 0 && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
            event.preventDefault();
            const delta = event.key === 'ArrowDown' ? 1 : -1;
            this.highlighted.set((this.highlighted() + delta + options) % options);
            return;
        }
        if (options > 0 && (event.key === 'Enter' || event.key === 'Tab') && !event.shiftKey) {
            event.preventDefault();
            if (this.slashOptions().length) this.chooseSlash(this.slashOptions()[this.highlighted()]);
            else this.chooseMention(this.mentionOptions()[this.highlighted()]);
            return;
        }
        if (event.key === 'Escape' && (this.mentionQuery() !== null || this.slashOptions().length)) {
            event.preventDefault();
            this.mentionQuery.set(null);
            if (this.slashOptions().length) this.setText('');
            return;
        }
        if (event.key === 'Tab' && !event.shiftKey && this.ghost()) {
            event.preventDefault();
            this.acceptGhost();
            return;
        }
        if (event.key === 'ArrowRight' && this.ghost() && this.caretAtEnd()) {
            event.preventDefault();
            this.setText(this.text() + nextWord(this.ghost()));
            return;
        }
        if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
            event.preventDefault();
            this.submit();
            return;
        }
        if (event.key === 'ArrowUp' && (this.text() === '' || this.historyIndex >= 0) && this.caretAtStart()) {
            const history = this.assistant.recentPrompts();
            if (this.historyIndex + 1 < history.length) {
                event.preventDefault();
                if (this.historyIndex < 0) this.draft = this.text();
                this.historyIndex++;
                this.setText(history[this.historyIndex]);
            }
            return;
        }
        if (event.key === 'ArrowDown' && this.historyIndex >= 0) {
            event.preventDefault();
            this.historyIndex--;
            this.setText(this.historyIndex >= 0 ? this.assistant.recentPrompts()[this.historyIndex] : this.draft);
        }
    }

    acceptGhost(): void {
        const ghost = this.ghost();
        if (!ghost) return;
        this.setText(this.text() + ghost);
    }

    chooseSlash(command: SlashCommand): void {
        if (command.prompt === null) {
            this.runLocal(command.command);
            return;
        }
        // Commands that take an argument wait for it; the rest send straight away.
        if (command.prompt.includes('{arg}')) this.setText(`${command.command} `);
        else {
            this.setText(command.command);
            this.submit();
        }
    }

    chooseMention(option: MentionOption): void {
        if (!option) return;
        const el = this.box()?.nativeElement;
        const caret = el?.selectionStart ?? this.text().length;
        const before = this.text()
            .slice(0, caret)
            .replace(/@([^\s@]{0,40})$/, `@${option.label} `);
        this.setText(before + this.text().slice(caret));
        this.mentions.update((list) => [...list, { type: option.type, id: option.id, label: option.label }]);
        this.mentionQuery.set(null);
    }

    removeMention(id: string): void {
        this.mentions.update((list) => list.filter((m) => m.id !== id));
    }

    submit(): void {
        const raw = this.text().trim();
        if (!raw || this.streaming()) return;
        if (raw === '/new' || raw === '/clear') {
            this.runLocal(raw);
            return;
        }
        const expanded = raw.startsWith('/') ? expandSlash(raw) : null;
        const mentions = this.mentions().filter((m) => raw.includes(`@${m.label}`) || raw.startsWith('/'));
        this.assistant.send(expanded ?? raw, mentions);
        this.setText('');
        this.mentions.set([]);
        this.historyIndex = -1;
    }

    private runLocal(command: string): void {
        if (command === '/new') this.assistant.startNew();
        this.setText('');
        this.mentions.set([]);
    }

    private setText(value: string): void {
        this.text.set(value);
        this.highlighted.set(0);
        const el = this.box()?.nativeElement;
        if (el) {
            el.value = value;
            el.setSelectionRange(value.length, value.length);
        }
        this.refreshPopovers();
        this.autosize();
    }

    private caretAtEnd(): boolean {
        const el = this.box()?.nativeElement;
        return !el || el.selectionStart === this.text().length;
    }

    private caretAtStart(): boolean {
        const el = this.box()?.nativeElement;
        return !el || el.selectionStart === 0 || this.historyIndex >= 0 || this.text() === '';
    }

    private autosize(): void {
        const el = this.box()?.nativeElement;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    }
}
